<template>
  <div class="ml-93">
    <el-row :gutter="20">
      <el-col class="integration-settings" :span="24">
        <div>
          <div v-if="integrationWizardIndex >= 1">
            <h3 class="cnct-jira-header">Connect to Jira Software Account</h3>
            <el-steps
              class="el-step-fixes"
              :active="integrationWizardIndex"
              finish-status="success"
            >
              <el-step></el-step>
              <el-step></el-step>
            </el-steps>
          </div>
          <el-carousel
            ref="wizard"
            :autoplay="false"
            :loop="false"
            indicator-position="none"
            arrow="never"
            height="400"
          >
            <el-carousel-item style="overflow: unset" :key="1">
              <span
                class="cnct-jira-header"
                style="margin-bottom: 22px"
              >Connect to Jira Software Account</span>
              <p class="top-header">
                This wizard walks you through the simple steps to connect Confluera CxDR to your Jira Software account. If you
                have multiple Jira Software accounts, simply run this wizard to configure each account.
              </p>

              <el-row type="flex" :gutter="24" style="margin: -32px 0px 0px;">
                <el-col :span="4" class="integration-logo-container">
                  <el-card shadow="hover" class="integration-logo-card">
                    <span class="display-block" style="margin-top: 14px; margin-left: 6px">
                      <img class="jira-logo" src="/static/images/logos/jira_image.svg" alt="jira logo" />
                    </span>
                  </el-card>
                </el-col>
                <el-col :span="20" class="connect-jira">
                  <el-row>
                    <el-row>
                      <el-col :span="23">
                        <span class="bold">Step 1</span>
                      </el-col>
                    </el-row>
                    <el-row>
                      <el-col :span="23">
                        Log into Jira console and enable the Jira APIs.
                        <br />Create a Jira Account.
                        <br />Create a key and download as JSON file.
                      </el-col>
                      <el-col>
                        <span
                          class="text-btn"
                          @click="loadDocumentation('page-cloud-jira')"
                        >Learn More</span>
                      </el-col>
                    </el-row>
                  </el-row>
                  <br />
                  <el-row>
                    <el-row>
                      <el-col :span="23">
                        <span class="bold">Step 2</span>
                      </el-col>
                    </el-row>
                    <el-row>
                      <el-col :span="23">Upload JSON to Confluera.</el-col>
                    </el-row>
                    <el-row>
                      <el-col style="margin-top: 25px;" :span="23">
                        <span class="bold">Step 3</span>
                      </el-col>
                    </el-row>
                    <el-row>
                      <el-col :span="23">Verify and submit.</el-col>
                    </el-row>
                  </el-row>
                </el-col>
              </el-row>
            </el-carousel-item>
            <el-carousel-item style="overflow: unset" :key="2">
              <div class="header-carousal-2">Connect Jira API to Confluera</div>
              <div class="box flex">
                <el-form
                  :model="cred"
                  ref="jiraIntegrationWizardRef"
                  label-position="left"
                  class="full-width"
                  :rules="jiraIntegrationRules"
                >                
                  <div>
                    <div class="flex">
                      <div class="mr-57">
                        <el-form-item label="Email" prop="emailId">
                          <el-input v-model="cred.emailId" placeholder="Enter your workspace-email"></el-input>
                        </el-form-item>
                      </div>
                      <div style="">
                        <el-form-item label="Jira API Token" prop="apiToken">
                          <el-input v-model="cred.apiToken" placeholder="Enter your API Token"></el-input>
                        </el-form-item>
                      </div>
                    </div>

                    <div class="flex">
                      <div class="mr-57">
                        <el-form-item label="Workspace URL" prop="workspaceUrl">
                          <el-input v-model="cred.workspaceUrl" placeholder="Enter your workspace URL"
                          ></el-input>
                        </el-form-item>
                      </div>
                      <div style="">
                        <el-form-item label="Jira Group / Project" prop="projectId">
                          <el-input v-model="cred.projectId" placeholder="Enter project ID."></el-input>
                        </el-form-item>
                      </div>
                    </div>
                  </div>
                </el-form>
              </div>
            </el-carousel-item>
          </el-carousel>
          <div class="flex">
            <span
              v-if="integrationWizardIndex >= 1"
              class="text-underline cursor-pointer back-button go-back-button"
              @click="backIntegrationStep"
            >Go Back</span>
            <el-button
              class="start-button"
              v-if="integrationWizardIndex < 1"
              size="medium"
              round
              @click="nextIntegrationStep"
            >Start</el-button>
            <el-button
              class="submit-button"
              v-else-if="integrationWizardIndex === 1"
              size="medium"
              round
              @click="saveJiraSettings"
            >Verify</el-button>
          </div>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import integrationFormMixin from '@/components/form/integrations/integrationFormMixin'
import loadDocumentationMixin from '@/mixins/loadDocumentationMixin'
import { JIRA_SETTING_SUCCESS, JIRA_SETTING_CREATE_REQUEST, JIRA_SETTING_REQUEST } from '@/store/modules/jira/jira-setting.type.js'
import { emailRe } from '@/utilities/regex/email'

export default {
  name: 'JiraSetting',
  mixins: [integrationFormMixin, loadDocumentationMixin],
  data() {
    return {
      copyJiraSettings: {},
      cred: {
        emailId: '',
        apiToken: '',
        workspaceUrl: '',
        projectId: ''
      },
      availableJiraProjects: [],
      jiraIntegrationRules: {
        workspaceUrl: [
          {
            required: true,
            trigger: 'blur',
            validator: (rule, value, callback) => {
              value = value.trim().toLowerCase()
              if (value.length) {
                callback()
              } else {
                callback(new Error("workspace url can't be blank"))
              }
            }
          }
        ],
        emailId: [
          {
            required: true,
            trigger: 'blur',
            validator: (rule, value, callback) => {
              value = value.trim().toLowerCase()
              if (value.length) {
                emailRe.test(value) ? callback() : callback(new Error('Invalid email format'))
              } else {
                callback(new Error("email can't be blank"))
              }
            }
          }
        ],
        apiToken: [
          {
            required: true,
            trigger: 'blur',
            validator: (rule, value, callback) => {
              value = value.trim().toLowerCase()
              if (value.length) {
                callback()
              } else {
                callback(new Error("api token can't be blank"))
              }
            }
          }
        ],
        projectId: [
          {
            required: true,
            trigger: 'blur',
            validator: (rule, value, callback) => {
              value = value.trim().toLowerCase()
              if (value.length) {
                callback()
              } else {
                callback(new Error("project id can't be blank"))
              }
            }
          }
        ]
      }
    }
  },
  methods: {
    saveJiraSettings() {
      this.$refs['jiraIntegrationWizardRef'].validate(valid => {
        if (valid) {
          let flag = false
          if (
            this.cred.emailId === '' ||
            this.cred.apiToken === '' ||
            this.cred.workspaceUrl === '' ||
            this.cred.projectId === '' ||
            this.availableJiraProjects.includes(this.cred.projectId)
          ) {
            flag = true
          }

          if (!flag) {
            this.$store.dispatch(JIRA_SETTING_CREATE_REQUEST, this.cred).then(data => {
              this.resetJiraSettings()
              this.copyJiraSettings = _.cloneDeep(this.jiraSettings)
              this.$store.dispatch(JIRA_SETTING_SUCCESS).then(_ => {
                this.resetJiraSettings()
                this.$message({
                  type: 'success',
                  message: 'Credential added successfully added'
                })
                this.loadingAllJiraProjects()
                this.$router.push({
                  name: 'preferences',
                  query: {
                    view: 'jira-configurations'}
                })
              })
            })
          } else {
            this.$message({
              type: 'error',
              message: 'Error while adding credentials'
            })
          }
        }
      })
    },
    resetJiraSettings() {
      this.cred.emailId = ''
      this.cred.apiToken = ''
      this.cred.projectId = ''
      this.cred.workspaceUrl = ''
    },
    loadingAllJiraProjects() {
      this.$store.dispatch(JIRA_SETTING_REQUEST).then(_ => {
        for (let entry of this.jiraSettings) {
          this.availableJiraProjects.push(entry.project_id)
        }
      })
    }
  },
  created() {
    this.loadingAllJiraProjects()
  },
  computed: {
    ...mapGetters(['jiraSettingReqStatus', 'jiraSettings'])
  },
  watch: {},
  components: {}
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
@import 'assets/styles/_variables.css';
@import 'assets/styles/integration_workflow.css';

.integration-settings {
  overflow: unset;
}

.initiated {
  h4 {
    margin-bottom: 40px;
  }

  p {
    text-align: center;
    width: 60%;
    margin: auto;
    display: block;
  }

  >>> .el-button {
    margin: auto;
  }

  >>> .el-icon-loading,
  .material-icons {
    font-size: 2em;
  }

  .center-text.error-icon {
    margin: auto;
    font-size: 2em;
  }
}

>>> .el-carousel--horizontal {
  overflow-x: hidden;
  width: 110%;
}

>>> .el-step__head.is-success {
  color: #1a90fa;
}

>>> .el-step__head {
  fill: #1a90fa;
  border-color: #1a90fa;
}

>>> .el-step__line {
  background-color: #1a90fa;
}

>>> .el-step__icon-inner {
  color: #1a90fa;
}

>>>.el-form-item {
  .el-form-item__content {
    width: 385px;
  }
}
.verify-details-box {
  border: 2px solid black;
  border-radius: 10px;
  margin-top: 4%;
  padding: 32px;
  height: 80%;
  overflow: auto;
}

.error-message {
  width: 60%;
  margin: 30px auto;

  p {
    width: 100%;
  }
}

.top-header {
  height: 94px;
  width: 834px;
  color: #454b60;
  font-size: 14px;
  letter-spacing: 0.17px;
  line-height: 22px;
}

.top-header-title {
  font-weight: 500px;
  font-size: 24px;
  margin-bottom: 22px;
}
.jira-content {
  height: 308px;
  width: 616px;
  color: #454b60;
  font-size: 14px;
  letter-spacing: 0;
  line-height: 22px;
}

.start-button {
  height: 36px;
  width: 113px;
  border-radius: 4px;
  background-color: #1a90fa;
  margin-left: 55%;
  text-align: center;
  margin-top: 50px;
}

.verify-button,
.submit-button {
  height: 36px;
  width: 113px;
  border-radius: 4px;
  background-color: #1a90fa;
  /* margin-left:55%; */
  text-align: center;
}

.go-back-button {
  height: 36px;
  width: 113px;
  border-radius: 4px;
  background-color: #ffffff;
  margin-left: 55%;
  text-align: center;
  border: 1px solid #767676;
  text-decoration: none;
}
.conf-block {
  display: inline-block;
  width: 34.86%;
  height: 27.34%;
  line-height: 20px;
  letter-spacing: 0.09px;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 50px;
}

.header-carousal-2 {
  height: 66px;
  width: 575px;
  color: #454b60;
  /* font-family: Montserrat; */
  font-size: 14px;
  letter-spacing: 0.1px;
  line-height: 22px;
}

.carousal-3-content {
  margin-bottom: 20px;
  font-size: 14px;
}

.integration-settings {
  .el-steps {
    padding: 20px 10px 25px 10px;
    margin: 0 0 20px 0;
    border-bottom: solid 1px #f0f0f0;
  }

  >>> .el-carousel {
    margin: 10px 0 30px 0;

    .el-carousel__container {
      height: 440px;
    }
  }
}

.cnct-jira-header {
  margin-bottom: 20px;
  margin-top: 10px;
  font-weight: 500px;
  font-size: 24px;
}

.el-step-fixes {
  width: 20%;
  padding-left: 0px;
  border: none;
}

.text-btn {
  cursor: pointer;
  color: #409eff;
}

.connect-jira {
  margin-left: 12px;
  width: 379px;
  height: 198px;
  font-size: 14px;
  letter-spacing: 0;
  line-height: 22px;
}

.jira-logo {
  margin-top: 2px;
  width: 89px;
  margin-left: 11px;
}
</style>



// WEBPACK FOOTER //
// src/views/admin/JiraSetting.vue